import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, ChevronDown } from "lucide-react";

export interface Artist {
  name: string;
  role?: string;
  image?: string;
  revealed: boolean;
  songLabel?: string;
  songUrl?: string;
  bio?: string;
  topSongs?: string[];
  spotifyUrl?: string;
}

interface ArtistCardProps {
  artist: Artist;
  index: number;
}

const TBACard = ({ index }: { index: number }) => {
  return (
    <motion.div
      className="relative aspect-square rounded-2xl overflow-hidden"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      {/* Dark base */}
      <div className="absolute inset-0 bg-yacht-charcoal" />

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(circle at 30% 40%, hsl(var(--yacht-blue)) 0%, transparent 50%), radial-gradient(circle at 70% 60%, hsl(var(--yacht-teal)) 0%, transparent 50%), radial-gradient(circle at 50% 80%, hsl(var(--yacht-deepteal)) 0%, transparent 60%)",
        }}
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />

      {/* Blurred silhouette */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="w-[55%] h-[70%] rounded-full relative"
          style={{
            background: "radial-gradient(ellipse at 50% 35%, rgba(255,255,255,0.08) 0%, transparent 70%)",
            filter: "blur(12px)",
          }}
        >
          <div
            className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[40%] aspect-square rounded-full"
            style={{ background: "rgba(255,255,255,0.06)", filter: "blur(8px)" }}
          />
          <div
            className="absolute top-[35%] left-1/2 -translate-x-1/2 w-[90%] h-[50%] rounded-t-full"
            style={{ background: "rgba(255,255,255,0.04)", filter: "blur(10px)" }}
          />
        </div>
      </div>

      {/* Gradient animated question mark */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <motion.span
          className="text-[20vw] md:text-[8vw] lg:text-[6vw] font-bold select-none"
          style={{
            background: "linear-gradient(135deg, hsl(var(--yacht-yellow)), hsl(var(--yacht-blue)), hsl(var(--yacht-yellow)))",
            backgroundSize: "200% 200%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 0 30px rgba(242, 220, 5, 0.3))",
          }}
          animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        >
          ?
        </motion.span>
      </div>

      {/* Noise texture */}
      <div
        className="absolute inset-0 opacity-[0.15] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Label */}
      <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
        <span className="text-white/40 text-xs font-semibold tracking-[0.3em] uppercase">
          Coming Soon
        </span>
      </div>
    </motion.div>
  );
};

const ArtistCard = ({ artist, index }: ArtistCardProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  if (!artist.revealed) {
    return <TBACard index={index} />;
  }

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!artist.songUrl || !audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleEnded = () => setIsPlaying(false);

  const handleCardClick = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <motion.div
      className="relative rounded-2xl overflow-hidden group w-full cursor-pointer"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onClick={handleCardClick}
    >
      <div className="relative aspect-square bg-yacht-charcoal overflow-hidden">
        {artist.image && (
          <img
            src={artist.image}
            alt={artist.name}
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.03]"
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-yacht-charcoal via-yacht-charcoal/20 to-transparent" />

        {artist.songUrl && (
          <>
            <audio ref={audioRef} src={artist.songUrl} onEnded={handleEnded} preload="none" />
            <button
              onClick={togglePlay}
              className="absolute top-5 right-5 z-20 w-14 h-14 rounded-full bg-yacht-yellow flex items-center justify-center shadow-xl transition-transform hover:scale-110 active:scale-95"
              aria-label={isPlaying ? "Pause song" : "Play song"}
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-yacht-charcoal" />
              ) : (
                <Play className="w-5 h-5 text-yacht-charcoal ml-0.5" />
              )}
            </button>
          </>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-10">
          <h3 className="text-white font-bold text-2xl md:text-4xl leading-tight">{artist.name}</h3>
          {artist.role && <p className="text-white/60 text-base md:text-lg mt-1">{artist.role}</p>}
          {artist.songLabel && (
            <p className="text-yacht-yellow text-sm font-medium mt-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-yacht-yellow animate-pulse" />
              {artist.songLabel}
            </p>
          )}
        </div>

        {/* Expand indicator */}
        {(artist.bio || artist.topSongs) && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10">
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown className="w-4 h-4 text-white/50" />
            </motion.div>
          </div>
        )}
      </div>

      {/* Expandable section */}
      <AnimatePresence>
        {isExpanded && (artist.bio || artist.topSongs) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="bg-yacht-charcoal px-6 pb-6 pt-4">
              {artist.bio && (
                <p
                  className="text-white/70 text-sm leading-relaxed"
                  style={{ fontFamily: "Georgia, serif" }}
                >
                  {artist.bio}
                </p>
              )}

              {artist.topSongs && artist.topSongs.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-yacht-yellow text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                    Known For
                  </h4>
                  <ul className="space-y-1.5">
                    {artist.topSongs.map((song, i) => (
                      <li key={song} className="flex items-center gap-2">
                        <Play className="w-3 h-3 text-yacht-yellow flex-shrink-0" />
                        <span className="text-white/80 text-sm">{song}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {artist.spotifyUrl && (
                <a
                  href={artist.spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yacht-yellow text-xs underline mt-3 inline-block"
                  onClick={(e) => e.stopPropagation()}
                >
                  Listen on Spotify
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ArtistCard;
